import { useEffect, useState } from "react";
import { Card, Table, Space, Tooltip, Button, message, Popconfirm, Row, Col } from "antd";
import { BsCaretRightFill, BsTrash } from "react-icons/bs";
import ReportTemplate from "./ReportTemplate";
import ReportHistory, { LogoImage } from "./ReportHistory";
import { useSearchColumn } from "Utility/hooks/useSearchColumn";
import { useReportList, useDeleteReort, useInstanceReport } from "../queries";
import { useAppSelector } from "store/hook";
import RunReport from "Components/Report/Periodic/RunReport";
import { ReportRecord } from "Utility/Types/report";
import ViewPDF from "./View_PDF";
import openNotificationWithIcon from "Components/Shared/Notification";
import { BsCardList } from "react-icons/bs";
import type { ColumnsType } from "antd/lib/table";
import { BsPencilSquare } from "react-icons/bs";

export function DateToFormattedString(a: number, utc: boolean = false) {
  const d = new Date(a);
  const yyyy = utc ? d.getUTCFullYear().toString() : d.getFullYear().toString();
  const mm = utc ? (d.getUTCMonth() + 1).toString() : (d.getMonth() + 1).toString(); // getMonth() is zero-based
  const dd = utc ? d.getUTCDate().toString() : d.getDate().toString();

  return yyyy + "-" + (mm[1] ? mm : "0" + mm[0]) + "-" + (dd[1] ? dd : "0" + dd[0]);
}

const PeriodicList = ({ isPeriodicActive }: { isPeriodicActive: boolean }) => {
  const { level, stateCid, loginCid } = useAppSelector((status) => status.global);
  const cid = level === "super_super" && stateCid !== "" ? stateCid : loginCid;
  const [LogoVisible, setLogoVisible] = useState(false);
  const [LogoUrl, setLogoUrl] = useState("");
  const getColumnSearchProps = useSearchColumn();
  const {
    data: reportTable,
    isLoading: reportListLoading,
    isFetching: reportListFetching,
    refetch: refetchReportList,
  } = useReportList(cid, stateCid);
  const { mutateAsync: deleteReportMutation, isLoading: deleteReportLoading } = useDeleteReort(stateCid);
  // const data = reportTable?.response?.[0]?.list.reverse() || [];
  const [recordIndex, setRecordIndex] = useState<number | undefined>(undefined);
  const [record, setRecord] = useState<ReportRecord | undefined>(undefined);
  const [reportHistoryVisible, setReportHistoryVisible] = useState(false);
  const deleteReport = (_id: string, name: string, delete_pdf: boolean, file_name: string) => {
    deleteReportMutation({ cid, name, _id, delete_pdf, file_name }).then((res) => console.log(res));
  };
  const [reportData, setReportData] = useState<ReportRecord[]>([]);

  useEffect(() => {
    if (reportTable?.response?.[0]?.list) {
      const reversedList = [...reportTable.response[0].list].reverse(); // 複製後再反轉，避免修改原陣列
      setReportData(reversedList as ReportRecord[]);
    }
  }, [reportListFetching, reportTable]); // 確保 reportTable 更新時也會觸發

  const [templateVisible, setTemplateVisible] = useState(false);
  const { mutateAsync: createInstanceReportMutation, isLoading } = useInstanceReport(stateCid);
  const [PDF, setPDF] = useState<File | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const runKey = "messageRunKey";
  useEffect(() => {
    if (isPeriodicActive && !reportListLoading) {
      refetchReportList();
    }
  }, [isPeriodicActive]);

  const columns: ColumnsType<ReportRecord> = [
    {
      title: "Template",
      dataIndex: "name",
      ...getColumnSearchProps("name"),
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
      responsive: ["xxl"],
      ...getColumnSearchProps("description"),
    },
    {
      title: "Period",
      // filters: typeFilter,
      filters: [
        {
          text: "Monthly",
          value: "monthly",
        },
        {
          text: "Weekly",
          value: "weekly",
        },
        {
          text: "Daily",
          value: "daily",
        },
      ],
      onFilter: (value, record) => record.report.duration.default_report?.periodic.indexOf(value as string) === 0,
      render: ({ report }) => {
        const period = report.duration.default_report?.periodic || "";
        return period.charAt(0)?.toUpperCase() + period.slice(1);
      },
    },
    // {
    //   title: "Status",
    //   // filters: typeFilter,
    //   render: ({ active }) => (active ? "Enabled" : "Disabled"),
    //   filters: [
    //     {
    //       text: "Enabled",
    //       value: true,
    //     },
    //     {
    //       text: "Disabled",
    //       value: false,
    //     },
    //   ],
    //   onFilter: (value, { active }) => active === value,
    // },
    {
      title: "Mail",
      responsive: ["xxl"],
      // filters: typeFilter,
      render: ({ mail: { active } }) => (active ? "Enabled" : "Disabled"),
      filters: [
        {
          text: "Enabled",
          value: true,
        },
        {
          text: "Disabled",
          value: false,
        },
      ],
      onFilter: (value, { mail: { active } }) => active === value,
    },
    {
      title: "Duration",
      // dataIndex: "description",
      // responsive: ["xxl"],
      render: (_: string, record: ReportRecord) => {
        const start = record.report?.duration?.start_utc;
        const end = record.report?.duration?.end_utc;
        return `${start ? DateToFormattedString(start * 1000) : "NA"} - ${
          end ? DateToFormattedString(end * 1000) : "NA"
        }`;
      },
    },
    {
      title: "Next Period",
      responsive: ["xxl"],
      // filters: typeFilter,
      render: (_: string, record: ReportRecord) => {
        const start = record.next_report_date?.start_utc;
        const end = record.next_report_date?.end_utc;
        return `${start ? DateToFormattedString(start * 1000) : "NA"} - ${
          end ? DateToFormattedString(end * 1000) : "NA"
        }`;
      },
    },
    {
      title: "Action",
      render: (_: string, record: ReportRecord, index) => {
        return (
          <Space size={0}>
            <Tooltip title="Run Now">
              <Button
                type="text"
                size="small"
                onClick={async () => {
                  message.info({ content: "Submit successfully. Please wait for messages.", key: runKey, duration: 2 });
                  message.loading({
                    content: "Submit successfully. Please wait for messages.",
                    key: runKey,
                    duration: 0,
                  });

                  try {
                    const { data } = await createInstanceReportMutation({
                      ...record,
                      demo: false,
                      cid,
                      report: { ...record?.report },
                    });

                    // 在顯示通知之前，先清除 loading 訊息
                    message.destroy(runKey);

                    if (data === "REPORT DONE") {
                      openNotificationWithIcon({
                        type: "success",
                        message: "Report Generated Successfully",
                        description: "A new report is generated, Please check report history",
                      });
                    } else {
                      openNotificationWithIcon({
                        type: "warning",
                        message: "No report is running",
                        // description: "Please check your duration setting",
                      });
                    }
                  } catch (error) {
                    // 捕捉異常並清除 loading 訊息
                    message.destroy(runKey);

                    // 顯示錯誤通知
                    openNotificationWithIcon({
                      type: "error",
                      message: "Report Generated failed",
                      description: "An error occurred while generating the report. Please try again.",
                    });
                  }
                }}
              >
                <BsCaretRightFill />
              </Button>
            </Tooltip>
            <Tooltip title="View Report History">
              <Button
                type="text"
                size="small"
                onClick={() => {
                  setRecord(record);
                  if (currentPage === 1) {
                    setRecordIndex(index);
                  } else {
                    setRecordIndex((currentPage - 1) * 10 + index);
                  }

                  setReportHistoryVisible(true);
                }}
              >
                <BsCardList />
              </Button>
            </Tooltip>
            <Tooltip title="Edit Template">
              <Button
                size="small"
                type="text"
                onClick={() => {
                  setRecord(record);
                  setTemplateVisible(true);
                }}
              >
                <BsPencilSquare />
              </Button>
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete all reports under this template?"
                onConfirm={() => {
                  deleteReport(record._id, record.name, true, record.file_name);
                }}
                onCancel={() => {
                  deleteReport(record._id, record.name, false, record.file_name);
                }}
                okText="Delete all"
                cancelText="Keep in repository"
              >
                <Button type="text" size="small">
                  <BsTrash />
                </Button>
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Card id="report">
      <ReportHistory
        setPDF={setPDF}
        record={record}
        reportHistoryVisible={reportHistoryVisible}
        setReportHistoryVisible={setReportHistoryVisible}
        setRecord={setRecord}
        setRecordIndex={setRecordIndex}
        recordIndex={recordIndex}
        data={reportData}
        loading={reportListLoading}
        fetching={reportListFetching}
        setLogoUrl={setLogoUrl}
        setLogoVisible={setLogoVisible}
      />
      <LogoImage LogoVisible={LogoVisible} setLogoVisible={setLogoVisible} LogoUrl={LogoUrl} />

      <ReportTemplate
        setRecord={setRecord}
        record={record}
        setPDF={setPDF}
        templateVisible={templateVisible}
        setTemplateVisible={setTemplateVisible}
      />

      <ViewPDF PDF={PDF} setPDF={setPDF} />

      <Row gutter={24} justify="start">
        <Col>
          <Button type="primary" onClick={() => setTemplateVisible(true)}>
            Create Template
          </Button>
        </Col>
      </Row>

      <Table
        loading={reportListLoading || deleteReportLoading || reportListFetching}
        columns={columns}
        dataSource={reportData}
        className="mt-3"
        rowKey={(record) => JSON.stringify(record)}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: reportData.length,
          onChange: (page) => setCurrentPage(page), // 更新當前頁數
        }}
      />
    </Card>
  );
};

export default PeriodicList;
